import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FileText, Download, ChevronDown, ChevronUp, Calendar, Clock, MapPin } from 'lucide-react';

interface Resident {
  name: string;
  age: number;
  address: string;
  emergencyContact: string;
  careLevel: string;
  primaryConditions: string[];
}

interface ActivityReportsProps {
  resident: Resident;
}

const ActivityReports: React.FC<ActivityReportsProps> = ({ resident }) => {
  const [reportPeriod, setReportPeriod] = useState('week');
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    'activity': true,
    'location': true,
    'sleep': true,
    'bathroom': true,
    'summary': true
  });

  // Mock data - in a real app this would come from an API or database
  const dailyActivityData = [
    { date: 'Mon', steps: 2100, roomChanges: 32, baseline: 2500, inactiveMinutes: 720 },
    { date: 'Tue', steps: 1800, roomChanges: 28, baseline: 2500, inactiveMinutes: 780 },
    { date: 'Wed', steps: 2300, roomChanges: 35, baseline: 2500, inactiveMinutes: 690 },
    { date: 'Thu', steps: 1900, roomChanges: 30, baseline: 2500, inactiveMinutes: 740 },
    { date: 'Fri', steps: 1700, roomChanges: 25, baseline: 2500, inactiveMinutes: 800 },
    { date: 'Sat', steps: 1500, roomChanges: 22, baseline: 2500, inactiveMinutes: 840 },
    { date: 'Sun', steps: 1400, roomChanges: 20, baseline: 2500, inactiveMinutes: 860 }
  ];

  const weeklyActivityData = [
    { week: 'Week 1', steps: 14000, roomChanges: 210, baseline: 17500, inactiveMinutes: 5200 },
    { week: 'Week 2', steps: 15200, roomChanges: 225, baseline: 17500, inactiveMinutes: 5000 },
    { week: 'Week 3', steps: 14500, roomChanges: 215, baseline: 17500, inactiveMinutes: 5100 },
    { week: 'Week 4', steps: 13700, roomChanges: 192, baseline: 17500, inactiveMinutes: 5300 }
  ];

  const locationData = [
    { name: 'Living Room', value: 42 },
    { name: 'Bedroom', value: 28 },
    { name: 'Kitchen', value: 15 },
    { name: 'Bathroom', value: 10 },
    { name: 'Other Areas', value: 5 }
  ];

  const nighttimeActivityData = [
    { day: 'Mon', bathroomVisits: 2, wakefulPeriods: 3, timeAwake: 45 },
    { day: 'Tue', bathroomVisits: 3, wakefulPeriods: 4, timeAwake: 65 },
    { day: 'Wed', bathroomVisits: 1, wakefulPeriods: 2, timeAwake: 30 },
    { day: 'Thu', bathroomVisits: 2, wakefulPeriods: 2, timeAwake: 35 },
    { day: 'Fri', bathroomVisits: 2, wakefulPeriods: 3, timeAwake: 50 },
    { day: 'Sat', bathroomVisits: 1, wakefulPeriods: 2, timeAwake: 25 },
    { day: 'Sun', bathroomVisits: 3, wakefulPeriods: 4, timeAwake: 60 }
  ];

  const bathroomVisitData = [
    { time: '6-9 AM', visits: 7, avgDuration: 4.5 },
    { time: '9-12 PM', visits: 3, avgDuration: 3.2 },
    { time: '12-3 PM', visits: 4, avgDuration: 3.8 },
    { time: '3-6 PM', visits: 5, avgDuration: 4.0 },
    { time: '6-9 PM', visits: 6, avgDuration: 4.2 },
    { time: '9-12 AM', visits: 4, avgDuration: 5.5 },
    { time: '12-6 AM', visits: 5, avgDuration: 6.0 }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const getActivityData = () => {
    return reportPeriod === 'week' ? dailyActivityData : weeklyActivityData;
  };

  const renderActivityBreakdown = () => {
    return (
      <div className="bg-white rounded-lg shadow mb-6">
        <div 
          className="p-4 border-b flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('activity')}
        >
          <h3 className="font-medium">Activity Breakdown</h3>
          {expandedSections.activity ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        {expandedSections.activity && (
          <div className="p-4">
            <div className="flex justify-between mb-4">
              <div className="space-x-2">
                <button 
                  onClick={() => setReportPeriod('week')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    reportPeriod === 'week' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  This Week
                </button>
                <button 
                  onClick={() => setReportPeriod('month')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    reportPeriod === 'month' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  This Month
                </button>
              </div>
              <button className="flex items-center text-sm text-blue-600 hover:underline">
                <Download size={16} className="mr-1" />
                Download Report
              </button>
            </div>
            
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-2">Daily Step Count</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={getActivityData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={reportPeriod === 'week' ? 'date' : 'week'} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="steps" 
                    stroke="#3b82f6" 
                    name="Steps"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="baseline" 
                    stroke="#9ca3af" 
                    name="Baseline"
                    strokeWidth={1}
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-2">Room Transitions</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getActivityData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={reportPeriod === 'week' ? 'date' : 'week'} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="roomChanges" fill="#8884d8" name="Room Changes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2">Inactive Time (minutes)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getActivityData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={reportPeriod === 'week' ? 'date' : 'week'} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="inactiveMinutes" fill="#f87171" name="Inactive Minutes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLocationBreakdown = () => {
    return (
      <div className="bg-white rounded-lg shadow mb-6">
        <div 
          className="p-4 border-b flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('location')}
        >
          <h3 className="font-medium">Location Patterns</h3>
          {expandedSections.location ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        {expandedSections.location && (
          <div className="p-4">
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2">Time Spent in Each Room (%)</h4>
              <div className="flex items-center">
                <ResponsiveContainer width="60%" height={250}>
                  <PieChart>
                    <Pie
                      data={locationData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {locationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                  </PieChart>
                </ResponsiveContainer>
                
                <div className="w-40">
                  {locationData.map((entry, index) => (
                    <div key={index} className="flex items-center mb-1">
                      <div 
                        className="w-3 h-3 mr-2"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-xs">{entry.name}: {entry.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2">Key Insights</h4>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li>Spending significantly more time in the living room (42%) compared to other areas</li>
                <li>Kitchen time is slightly below average for the past month, may indicate less meal preparation</li>
                <li>Bathroom visits show consistent pattern with previous weeks</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSleepPatterns = () => {
    return (
      <div className="bg-white rounded-lg shadow mb-6">
        <div 
          className="p-4 border-b flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('sleep')}
        >
          <h3 className="font-medium">Sleep & Nighttime Activity</h3>
          {expandedSections.sleep ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        {expandedSections.sleep && (
          <div className="p-4">
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-2">Nighttime Disruptions</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={nighttimeActivityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="bathroomVisits" fill="#3b82f6" name="Bathroom Visits" />
                  <Bar dataKey="wakefulPeriods" fill="#f87171" name="Wakeful Periods" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2">Minutes Awake During Night</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={nighttimeActivityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="timeAwake" 
                    stroke="#8b5cf6" 
                    name="Minutes Awake"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBathroomVisits = () => {
    return (
      <div className="bg-white rounded-lg shadow mb-6">
        <div 
          className="p-4 border-b flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('bathroom')}
        >
          <h3 className="font-medium">Bathroom Visit Analysis</h3>
          {expandedSections.bathroom ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        {expandedSections.bathroom && (
          <div className="p-4">
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-2">Bathroom Visits by Time of Day</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={bathroomVisitData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="visits" fill="#3b82f6" name="Number of Visits" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2">Average Duration by Time (minutes)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={bathroomVisitData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="avgDuration" 
                    stroke="#10b981" 
                    name="Avg. Duration (min)"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSummaryInsights = () => {
    return (
      <div className="bg-white rounded-lg shadow">
        <div 
          className="p-4 border-b flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('summary')}
        >
          <h3 className="font-medium">Summary & Insights</h3>
          {expandedSections.summary ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        {expandedSections.summary && (
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium mb-3">Key Activity Insights</h4>
                <ul className="space-y-4">
                  <li className="flex">
                    <div className="flex-shrink-0 mr-3">
                      <Clock className="text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">Activity Trend</p>
                      <p className="text-sm text-gray-600">
                        Overall activity level is down 12% compared to previous month. Daily step count 
                        has decreased from an average of 2,300 to 1,800.
                      </p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0 mr-3">
                      <Calendar className="text-purple-500" />
                    </div>
                    <div>
                      <p className="font-medium">Routine Changes</p>
                      <p className="text-sm text-gray-600">
                        Morning routine has been consistent, but afternoon activity has declined.
                        Spending longer inactive periods in the living room compared to previous weeks.
                      </p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0 mr-3">
                      <MapPin className="text-green-500" />
                    </div>
                    <div>
                      <p className="font-medium">Location Pattern Changes</p>
                      <p className="text-sm text-gray-600">
                        Kitchen visits have decreased by 15%, which may indicate changes in 
                        meal preparation habits or eating patterns.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-3">Recommendations</h4>
                <ul className="space-y-4 text-sm">
                  <li className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                    <p className="font-medium">Schedule afternoon activity check-in</p>
                    <p className="text-gray-600 mt-1">
                      Consider scheduling a caregiver check-in between 2-4 PM to encourage
                      more movement and activity during typical low-activity periods.
                    </p>
                  </li>
                  <li className="p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                    <p className="font-medium">Nutritional assessment</p>
                    <p className="text-gray-600 mt-1">
                      Decreased kitchen activity may indicate nutrition concerns.
                      Consider a meal preparation assessment during the next visit.
                    </p>
                  </li>
                  <li className="p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                    <p className="font-medium">Sleep improvement strategies</p>
                    <p className="text-gray-600 mt-1">
                      Nighttime bathroom visits are increasing. Consider reviewing
                      fluid intake timing and medication schedule.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium mb-2">Overall Assessment</h4>
              <p className="text-sm text-gray-600">
                {resident.name} is maintaining independence but showing early signs of activity decline 
                that warrant attention. The floor sensors indicate a 12% reduction in overall movement 
                and mobility compared to baseline. Bathroom visit patterns suggest potential UTI concerns 
                or medication side effects. Recommended actions include scheduling a wellness check, 
                reviewing medications, and increasing afternoon engagement opportunities.
              </p>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                <Download size={16} className="mr-2" />
                Download Full Report
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold">Activity Reports</h3>
          <p className="text-gray-600">Detailed activity analysis for {resident.name}</p>
        </div>
        <button className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
          <FileText size={16} className="mr-2" />
          Generate New Report
        </button>
      </div>
      
      {renderActivityBreakdown()}
      {renderLocationBreakdown()}
      {renderSleepPatterns()}
      {renderBathroomVisits()}
      {renderSummaryInsights()}
    </div>
  );
};

export default ActivityReports; 